import { jsPDF } from 'jspdf';
import { FlashCard } from '../lib/gemini';

export function downloadFlashcards(flashcards: FlashCard[], filename: string = 'flashcards') {
  if (!flashcards.length) return;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const appName = 'EcstaticLearn';
  const primaryColor = [40, 60, 120];
  const secondaryColor = [120, 120, 120];

  // --- Cover Page ---
  doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.rect(0, 0, pageWidth, 120, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(36);
  doc.setTextColor(255, 255, 255);
  doc.text('Study Flashcards', pageWidth / 2, 70, { align: 'center' });
  doc.setFontSize(18);
  doc.text(`${flashcards.length} Card${flashcards.length !== 1 ? 's' : ''}`, pageWidth / 2, 105, { align: 'center' });
  doc.setFontSize(14);
  doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
  doc.text(`Generated with ${appName}`, pageWidth / 2, 150, { align: 'center' });
  doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
  doc.setLineWidth(2);
  doc.line(margin, 170, pageWidth - margin, 170);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(0, 0, 0);
  doc.text('Print double-sided for best results. Cut along the lines to create individual cards.', pageWidth / 2, 200, { align: 'center' });
  doc.addPage();

  // --- Flashcards ---
  flashcards.forEach((card, i) => {
    // Card number
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text(`Card ${i + 1} of ${flashcards.length}`, margin, margin + 10);

    // Term (front)
    doc.setFontSize(12);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.text('TERM', margin, margin + 40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 0);
    doc.text(card.term, margin, margin + 70, { maxWidth: pageWidth - 2 * margin });

    // Divider
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(1);
    doc.line(margin, margin + 90, pageWidth - margin, margin + 90);

    // Definition (back)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.text('DEFINITION', margin, margin + 120);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(card.definition, margin, margin + 150, { maxWidth: pageWidth - 2 * margin });

    // Footer
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
    doc.text(appName, pageWidth - margin, pageHeight - 20, { align: 'right' });
    doc.text(`Page ${i + 2} of ${flashcards.length + 1}`, margin, pageHeight - 20);

    if (i < flashcards.length - 1) doc.addPage();
  });

  doc.save(`${filename}.pdf`);
}

export function downloadFlashcardsAsPDF(flashcards: FlashCard[], filename: string = 'flashcards') {
  generateAndDownloadPDF(flashcards, filename);
}

async function generateAndDownloadPDF(flashcards: FlashCard[], filename: string) {
  try {
    // Create a canvas for PDF generation
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas context not available');
    }

    // Set canvas size for A4 format (595 x 842 points)
    canvas.width = 595;
    canvas.height = 842;
    
    // Set background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // For now, we'll create a styled HTML that can be printed as PDF
    // In a production environment, you'd use a library like jsPDF or pdfmake
    const htmlContent = generatePrintableHTML(flashcards, filename);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating PDF:', error);
    // Fallback to HTML download
    const htmlContent = generateFlashcardHTML(flashcards, filename);
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

function generatePrintableHTML(flashcards: FlashCard[], filename: string): string {
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: white;
      color: #1e293b;
      line-height: 1.6;
      padding: 20px;
      margin: 0;
    }
    
    .container {
      max-width: 800px;
      margin: 0 auto;
    }
    
    .header {
      text-align: center;
      margin-bottom: 30px;
      padding: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    
    .header h1 {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 8px;
    }
    
    .header p {
      font-size: 1rem;
      opacity: 0.9;
    }
    
    .flashcard {
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      border: 2px solid #e2e8f0;
      margin-bottom: 20px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    .card-front, .card-back {
      padding: 25px;
      min-height: 150px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    
    .card-front {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      position: relative;
    }
    
    .card-front::before {
      content: 'TERM';
      position: absolute;
      top: 15px;
      left: 15px;
      font-size: 0.7rem;
      font-weight: 600;
      opacity: 0.8;
      letter-spacing: 0.05em;
    }
    
    .card-back {
      background: white;
      color: #1e293b;
      border-top: 1px solid #e2e8f0;
    }
    
    .card-back::before {
      content: 'DEFINITION';
      position: absolute;
      top: 15px;
      left: 15px;
      font-size: 0.7rem;
      font-weight: 600;
      color: #64748b;
      letter-spacing: 0.05em;
    }
    
    .card-content {
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.6;
      text-align: center;
      word-wrap: break-word;
    }
    
    .card-number {
      position: absolute;
      bottom: 15px;
      right: 15px;
      font-size: 0.8rem;
      opacity: 0.6;
      font-weight: 500;
    }
    
    .footer {
      text-align: center;
      padding: 20px;
      background: #f1f5f9;
      border-radius: 12px;
      margin-top: 30px;
    }
    
    .footer h3 {
      color: #475569;
      margin-bottom: 10px;
      font-size: 1.1rem;
    }
    
    .footer p {
      color: #64748b;
      font-size: 0.9rem;
    }
    
    @media print {
      body {
        background: white;
        padding: 0;
        margin: 0;
      }
      
      .container {
        max-width: none;
        margin: 0;
        padding: 15px;
      }
      
      .flashcard {
        break-inside: avoid;
        page-break-inside: avoid;
        margin-bottom: 15px;
      }
      
      .header, .footer {
        break-inside: avoid;
        page-break-inside: avoid;
        margin-bottom: 15px;
      }
      
      .header {
        margin-bottom: 20px;
      }
    }
  `;

  const cardsHTML = flashcards.map((card, index) => `
    <div class="flashcard">
      <div class="card-front">
        <div class="card-content">${escapeHtml(card.term)}</div>
        <div class="card-number">${index + 1}</div>
      </div>
      <div class="card-back">
        <div class="card-content">${escapeHtml(card.definition)}</div>
        <div class="card-number">${index + 1}</div>
      </div>
    </div>
  `).join('');

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Flashcards - ${escapeHtml(filename)}</title>
        <style>${css}</style>
    </head>
    <body>
        <div class="container">
            <div class="header">
                <h1>📚 Study Flashcards</h1>
                <p>${flashcards.length} card${flashcards.length !== 1 ? 's' : ''} generated with EcstaticLearn</p>
            </div>
            
            <div class="flashcards-container">
                ${cardsHTML}
            </div>
            
            <div class="footer">
                <h3>📖 Study Tips</h3>
                <p>Print these cards double-sided for best results • Cut along the lines to create individual cards • Use spaced repetition for optimal learning</p>
            </div>
        </div>
    </body>
    </html>
  `;
}

function generateFlashcardHTML(flashcards: FlashCard[], filename: string): string {
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #f8fafc;
      color: #1e293b;
      line-height: 1.6;
      padding: 20px;
    }
    
    .container {
      max-width: 1200px;
      margin: 0 auto;
    }
    
    .header {
      text-align: center;
      margin-bottom: 40px;
      padding: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-radius: 12px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    
    .header h1 {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 8px;
    }
    
    .header p {
      font-size: 1.1rem;
      opacity: 0.9;
    }
    
    .flashcards-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
      gap: 30px;
      margin-bottom: 40px;
    }
    
    .flashcard {
      background: white;
      border-radius: 16px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      border: 1px solid #e2e8f0;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    .flashcard:hover {
      transform: translateY(-2px);
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    }
    
    .card-front, .card-back {
      padding: 30px;
      min-height: 200px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    
    .card-front {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      position: relative;
    }
    
    .card-front::after {
      content: '';
      position: absolute;
      top: 20px;
      right: 20px;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .card-front::before {
      content: 'TERM';
      position: absolute;
      top: 20px;
      left: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      opacity: 0.8;
      letter-spacing: 0.05em;
    }
    
    .card-back {
      background: white;
      color: #1e293b;
      border-top: 1px solid #e2e8f0;
    }
    
    .card-back::before {
      content: 'DEFINITION';
      position: absolute;
      top: 20px;
      left: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      letter-spacing: 0.05em;
    }
    
    .card-content {
      font-size: 1.1rem;
      font-weight: 500;
      line-height: 1.6;
      text-align: center;
      word-wrap: break-word;
    }
    
    .card-number {
      position: absolute;
      bottom: 20px;
      right: 20px;
      font-size: 0.875rem;
      opacity: 0.6;
      font-weight: 500;
    }
    
    .footer {
      text-align: center;
      padding: 30px;
      background: #f1f5f9;
      border-radius: 12px;
      margin-top: 40px;
    }
    
    .footer h3 {
      color: #475569;
      margin-bottom: 10px;
    }
    
    .footer p {
      color: #64748b;
      font-size: 0.9rem;
    }
    
         @media print {
       body {
         background: white;
         padding: 0;
         margin: 0;
       }
       
       .container {
         max-width: none;
         margin: 0;
         padding: 20px;
       }
       
       .flashcards-grid {
         grid-template-columns: repeat(2, 1fr);
         gap: 20px;
         margin-bottom: 20px;
       }
       
       .flashcard {
         break-inside: avoid;
         page-break-inside: avoid;
         margin-bottom: 20px;
       }
       
       .header, .footer {
         break-inside: avoid;
         page-break-inside: avoid;
         margin-bottom: 20px;
       }
       
       .header {
         margin-bottom: 30px;
       }
     }
    
    @media (max-width: 768px) {
      .flashcards-grid {
        grid-template-columns: 1fr;
        gap: 20px;
      }
      
      .header h1 {
        font-size: 2rem;
      }
      
      .card-content {
        font-size: 1rem;
      }
    }
  `;

  const cardsHTML = flashcards.map((card, index) => `
    <div class="flashcard">
      <div class="card-front">
        <div class="card-content">${escapeHtml(card.term)}</div>
        <div class="card-number">${index + 1}</div>
      </div>
      <div class="card-back">
        <div class="card-content">${escapeHtml(card.definition)}</div>
        <div class="card-number">${index + 1}</div>
      </div>
    </div>
  `).join('');

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Flashcards - ${escapeHtml(filename)}</title>
        <style>${css}</style>
    </head>
    <body>
        <div class="container">
            <div class="header">
                <h1>📚 Study Flashcards</h1>
                <p>${flashcards.length} cards generated with EcstaticLearn</p>
            </div>
            
            <div class="flashcards-grid">
                ${cardsHTML}
            </div>
            
            <div class="footer">
                <h3>📖 Study Tips</h3>
                <p>Print these cards double-sided for best results • Cut along the lines to create individual cards • Use spaced repetition for optimal learning</p>
            </div>
        </div>
    </body>
    </html>
  `;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
} 